import React from "react";

import { useAssetManagerWorkspace } from "../../hooks/useAssetManagerWorkspace";
import {
  currentNavBdt,
  formatDecimal,
  formatBdtAmount,
  pnlNavDenominatorShares,
  targetFundAfterPnlBdt,
  totalShareCap,
} from "../../utils/assetManagerPnl";
import { projectNetAssetsIssuedAtNav } from "../../utils/investmentPricing";
import { projectTitleWithCode } from "../../utils/projectViews";

export default function PnlSubmitModal() {
  const ws = useAssetManagerWorkspace();
  const {
    pnlModalProject,
    closePnlModal,
    pnlEditingId,
    pnlSubmitError,
    onSubmitPnl,
    pnlForm,
    setPnlForm,
    pnlRows,
    adminFeePct,
    computedAdminFeeBdt,
    computedNetPnl,
    sharePriceAfterSubmissionBdt,
    netAssetsOnIssuedAfterPnlPreview,
    pnlFundValueManual,
    setPnlFundValueManual,
    pnlSubmitting,
    projectMetrics,
  } = ws;

  if (!pnlModalProject) return null;

  const earliestIso = projectMetrics.earliestPnlSubmissionDateIso(pnlModalProject);

  return (
    <div className="modal-backdrop" role="presentation" onClick={closePnlModal}>
      <div
        className="modal-card modal-card-elevated"
        style={{ maxWidth: 560, width: "100%", maxHeight: "90vh", overflow: "auto" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pnl-modal-title"
      >
        <div
          style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 8 }}
        >
          <h3 id="pnl-modal-title" style={{ margin: 0 }}>
            {pnlEditingId ? "Edit P&L submission" : "Submit Project P&L"}
          </h3>
          <button type="button" className="btn-secondary compact-btn" onClick={closePnlModal} aria-label="Close">
            Close
          </button>
        </div>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          Submissions are sent to admin review before NAV updates apply.
        </p>
        <p style={{ margin: "8px 0 12px", fontSize: 15 }}>
          <strong>{projectTitleWithCode(pnlModalProject)}</strong>
        </p>
        {pnlSubmitError ? (
          <p className="msg msg-error" role="alert" style={{ margin: "0 0 12px" }}>
            {pnlSubmitError}
          </p>
        ) : null}
        <form className="stack" onSubmit={onSubmitPnl}>
          <div>
            <label className="muted" style={{ display: "block", fontSize: 12, marginBottom: 4 }} htmlFor="pnl-submission-date">
              Submission date (as of)
            </label>
            <input
              id="pnl-submission-date"
              className="compact-field"
              type="date"
              min={earliestIso || undefined}
              value={pnlForm.submission_date}
              onChange={(e) => {
                const raw = e.target.value;
                const next = projectMetrics.clampPnlSubmissionDateIso(raw, pnlModalProject);
                setPnlForm((prev) => ({ ...prev, submission_date: next }));
              }}
              required
            />
            <small className="muted" style={{ display: "block", marginTop: 4, fontSize: 11 }}>
              Earliest allowed: <strong>{earliestIso || "—"}</strong>
            </small>
          </div>
          <div className="grid-2">
            <div>
              <label className="muted" style={{ display: "block", fontSize: 12, marginBottom: 4 }} htmlFor="pnl-revenue">
                Revenue (BDT)
              </label>
              <input
                id="pnl-revenue"
                className="compact-field"
                type="number"
                step="0.01"
                min="0"
                value={pnlForm.revenue}
                onChange={(e) => setPnlForm((prev) => ({ ...prev, revenue: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="muted" style={{ display: "block", fontSize: 12, marginBottom: 4 }} htmlFor="pnl-expenses">
                Expenses (BDT)
              </label>
              <input
                id="pnl-expenses"
                className="compact-field"
                type="number"
                step="0.01"
                min="0"
                value={pnlForm.expenses}
                onChange={(e) => setPnlForm((prev) => ({ ...prev, expenses: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="muted" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
                Platform admin fee rate
              </label>
              <input className="compact-field" readOnly tabIndex={-1} value={`${adminFeePct}%`} style={{ background: "#f8fafc", color: "#475569" }} />
            </div>
            <div>
              <label className="muted" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
                Admin fee amount (BDT)
              </label>
              <input className="compact-field" readOnly tabIndex={-1} value={computedAdminFeeBdt} style={{ background: "#f8fafc", color: "#475569" }} />
            </div>
          </div>
          <div className="grid-2">
            <div>
              <label className="muted" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
                Net P&amp;L after admin fee
              </label>
              <input className="compact-field" readOnly tabIndex={-1} value={computedNetPnl} style={{ background: "#f8fafc", color: "#475569" }} />
            </div>
            <div>
              <label className="muted" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
                Target fund after P&amp;L (BDT)
              </label>
              <input
                className="compact-field"
                type="number"
                step="0.01"
                min="0"
                value={pnlForm.updated_fund_value}
                onChange={(e) => {
                  setPnlFundValueManual(true);
                  setPnlForm((prev) => ({ ...prev, updated_fund_value: e.target.value }));
                }}
              />
              <button
                type="button"
                className="btn-secondary compact-btn"
                style={{ marginTop: 6, fontSize: 12 }}
                onClick={() => {
                  setPnlFundValueManual(false);
                  const nextVal = targetFundAfterPnlBdt(pnlModalProject, computedNetPnl);
                  if (nextVal == null) return;
                  setPnlForm((prev) => ({ ...prev, updated_fund_value: nextVal.toFixed(2) }));
                }}
              >
                Reset to suggested
              </button>
            </div>
          </div>
          <div>
            <label className="muted" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
              Share price after submission (BDT / share)
            </label>
            <input
              className="compact-field"
              readOnly
              tabIndex={-1}
              value={sharePriceAfterSubmissionBdt == null ? "—" : formatDecimal(sharePriceAfterSubmissionBdt, 2)}
              style={{ background: "#f8fafc", color: "#475569", fontWeight: 600 }}
            />
          </div>
          <div>
            <label className="muted" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
              Narrative (optional)
            </label>
            <textarea
              className="compact-field"
              rows={3}
              value={pnlForm.narrative}
              onChange={(e) => setPnlForm((prev) => ({ ...prev, narrative: e.target.value }))}
            />
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="btn-secondary" onClick={closePnlModal}>
              Cancel
            </button>
            <button type="submit" style={{ flex: "1 1 200px" }} disabled={pnlSubmitting}>
              {pnlSubmitting ? (pnlEditingId ? "Saving…" : "Submitting…") : pnlEditingId ? "Save changes" : "Submit P&L"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


